import React from 'react'

export interface SegmentedControlOption {
  id: string
  label: string
  icon?: string
  badge?: string
}

export interface SegmentedControlProps {
  options: Array<SegmentedControlOption | string>
  value: string
  onChange: (value: string) => void
  className?: string
  style?: React.CSSProperties
}

const CSS = `
.wst-seg-control {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border-radius: var(--radius-full, 9999px);
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.3);
  user-select: none;
  box-sizing: border-box;
}

.wst-seg-control__indicator {
  position: absolute;
  top: 4px;
  bottom: 4px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius-full, 9999px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.15);
  transition: all 400ms cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: none;
  z-index: 1;
}

.wst-seg-control__btn {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 110px;
  padding: 7px 16px;
  border-radius: var(--radius-full, 9999px);
  border: none;
  background: transparent;
  font-family: var(--font-body, system-ui);
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition: color 200ms ease;
  white-space: nowrap;
}
.wst-seg-control__btn:hover {
  color: rgba(255, 255, 255, 0.9);
}
.wst-seg-control__btn--active {
  color: #ffffff;
  font-weight: 600;
}
.wst-seg-control__badge {
  font-size: 10px;
  font-family: var(--font-data);
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 9999px;
  background: var(--c-accent-electric, #38BFFF);
  color: var(--c-ink, #0B1230);
  line-height: 1.2;
}
`

if (typeof document !== 'undefined' && !document.getElementById('wst-seg-control-css')) {
  const s = document.createElement('style')
  s.id = 'wst-seg-control-css'
  s.textContent = CSS
  document.head.appendChild(s)
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options = [],
  value,
  onChange,
  className = '',
  style = {},
}) => {
  const normalized: SegmentedControlOption[] = options.map((opt) =>
    typeof opt === 'string' ? { id: opt, label: opt } : opt,
  )

  const activeIndex = Math.max(
    0,
    normalized.findIndex((opt) => opt.id === value),
  )

  const count = normalized.length || 1
  const stepWidth = 100 / count

  return (
    <div className={`wst-seg-control ${className}`} style={style} role="tablist">
      {count > 0 && (
        <div
          className="wst-seg-control__indicator"
          style={{
            left: `calc(${activeIndex * stepWidth}% + 4px)`,
            width: `calc(${stepWidth}% - 8px)`,
          }}
        />
      )}
      {normalized.map((opt) => {
        const isActive = opt.id === value
        return (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`wst-seg-control__btn ${isActive ? 'wst-seg-control__btn--active' : ''}`}
            onClick={() => onChange && onChange(opt.id)}
          >
            {opt.icon && <i className={`pi ${opt.icon}`} style={{ fontSize: '0.9em' }} />}
            <span>{opt.label}</span>
            {opt.badge && <span className="wst-seg-control__badge">{opt.badge}</span>}
          </button>
        )
      })}
    </div>
  )
}
